import { DesignData, ProductVariant, Spread } from 'src/store/design/types';
import useIntegrationLayer from 'src/util/hook/useIntegrationLayer';

/**
 * Gets available product UIDs from integration layer
 * @returns Set of available product UIDs, or empty set if none available
 */
const getAvailableProductUids = (): Set<string> => {
  const integrationLayer = useIntegrationLayer();
  const availableProducts = integrationLayer?.abstractProduct?.products || [];

  if (!availableProducts.length) {
    return new Set();
  }

  return new Set(
    availableProducts
      .map((product) => product?.productUid)
      .filter((uid): uid is string => Boolean(uid)),
  );
};

/**
 * Filters variants to only include those with product UIDs in the available set
 * @param variants - Array of product variants
 * @param availableProductUids - Set of available product UIDs
 * @returns Filtered array of variants
 */
const filterVariantsByAvailableProducts = (
  variants: ProductVariant[],
  availableProductUids: Set<string>,
): ProductVariant[] => variants.filter((variant) => availableProductUids.has(variant.product_uid));

/**
 * Collects all available print areas from variants
 * @param variants - Array of product variants
 * @returns Set of available print area names
 */
const getAvailablePrintAreas = (variants: ProductVariant[]): Set<string> => {
  const availablePrintAreas = new Set<string>();
  variants.forEach((variant) => {
    variant.types.forEach((type) => availablePrintAreas.add(type));
  });
  return availablePrintAreas;
};

/**
 * Filters spreads to only include those matching available print areas from variants
 * @param spreads - Array of spreads
 * @param variants - Array of product variants
 * @returns Filtered array of spreads
 */
const filterSpreadsByAvailablePrintAreas = (
  spreads: Spread[],
  variants: ProductVariant[],
): Spread[] => {
  const availablePrintAreas = getAvailablePrintAreas(variants);
  return spreads.filter((spread) => availablePrintAreas.has(spread.name));
};

/**
 * Removes unsupported print areas from design structure based on available products
 * 1. Filters variants to only include product UIDs available in the integration layer
 * 2. Filters spreads to only include those matching available print areas from filtered variants
 *
 * @param designData - The design structure to filter
 * @returns The filtered design structure, or original if no variants or no products available
 */
const removeUnsupportedPrintAreas = (designData: DesignData): DesignData => {
  // If no design data or no variants, return as-is
  if (!designData?.variants?.length) {
    return designData;
  }

  // Get available product UIDs from integration layer
  const availableProductUids = getAvailableProductUids();

  if (!availableProductUids.size) {
    return designData;
  }

  const filteredVariants = filterVariantsByAvailableProducts(designData.variants, availableProductUids);

  if (!filteredVariants.length) {
    return designData;
  }

  const filteredSpreads = filterSpreadsByAvailablePrintAreas(designData.spreads, filteredVariants);

  if (!filteredSpreads.length) {
    return designData;
  }

  return {
    ...designData,
    variants: filteredVariants,
    spreads: filteredSpreads,
  };
};

export default removeUnsupportedPrintAreas;
